import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { toast } from 'react-toastify';
import { 
    ClockIcon, 
    CheckBadgeIcon, 
    XCircleIcon, 
    TruckIcon, 
    ArrowUturnLeftIcon,
    ExclamationTriangleIcon 
} from '@heroicons/react/20/solid';

const getStatusInfo = (status) => {
    switch (status) {
        case 'pending': 
        case 'pending_approval': return { label: 'Onay Bekleniyor', color: 'bg-yellow-100 text-yellow-800', icon: ClockIcon };
        case 'approved': return { label: 'Onaylandı, Kargo Bekleniyor', color: 'bg-blue-100 text-blue-800', icon: CheckBadgeIcon };
        case 'customer_shipped':
        case 'in_transit':
        case 'shipping': return { label: 'Kargoda', color: 'bg-purple-100 text-purple-800', icon: TruckIcon };
        case 'received': return { label: 'Mağazaya Ulaştı', color: 'bg-indigo-100 text-indigo-800', icon: CheckBadgeIcon };
        case 'under_review': return { label: 'İncelemede', color: 'bg-orange-100 text-orange-800', icon: ClockIcon };
        case 'review_completed': return { label: 'İnceleme Tamamlandı', color: 'bg-blue-100 text-blue-800', icon: CheckBadgeIcon };
        case 'refund_approved': return { label: 'İade Onaylandı (Ücret İadesi Bekleniyor)', color: 'bg-emerald-100 text-emerald-800', icon: CheckBadgeIcon };
        case 'refund_completed':
        case 'completed': return { label: 'İade Tamamlandı', color: 'bg-green-100 text-green-800', icon: CheckBadgeIcon };
        case 'rejected': return { label: 'Reddedildi', color: 'bg-red-100 text-red-800', icon: XCircleIcon };
        case 'cancelled': return { label: 'İptal Edildi', color: 'bg-red-100 text-red-800', icon: XCircleIcon };
        default: return { label: 'Bilinmiyor', color: 'bg-gray-100 text-gray-800', icon: ClockIcon };
    }
};

const ReturnsTab = ({ orderToReturn, onReturnCreated, onCancelReturnCreation }) => {
    const { token } = useAuth();
    const { addToCart } = useCart();
    const [returns, setReturns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedReturnId, setExpandedReturnId] = useState(null);
    
    const isCreateMode = !!orderToReturn;
    
    // Create form states
    const [selectedItems, setSelectedItems] = useState([]);
    const [reasons, setReasons] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    const RETURN_REASONS = [
        'Beden uymadı',
        'Beklediğim gibi değil',
        'Hasarlı ürün',
        'Yanlış ürün gönderilmiş',
        'Kalitesini beğenmedim',
        'Diğer'
    ];

    useEffect(() => {
        if (!isCreateMode) {
            fetchReturns();
        }
    }, [isCreateMode, token]);

    const fetchReturns = async () => {
        try {
            setLoading(true);
            const data = await api('/api/returns', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setReturns(data || []);
        } catch (error) {
            toast.error('İade talepleri yüklenirken hata oluştu.');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateReturn = async () => {
        if (selectedItems.length === 0) {
            toast.warning('Lütfen iade edilecek en az bir ürün seçin.');
            return;
        }
        if (reasons.length === 0) {
            toast.warning('Lütfen iade nedeni seçin.');
            return;
        }

        try {
            setSubmitting(true);
            await api.post('/api/returns', {
                order_id: orderToReturn.id,
                reason: reasons.join(', '),
                items: selectedItems.map(item => ({
                    product_id: item.product_id,
                    variant_id: item.variant_id,
                    quantity: item.quantity
                }))
            }, { headers: { Authorization: `Bearer ${token}` } });

            toast.success('İade talebiniz başarıyla oluşturuldu.');
            setSelectedItems([]);
            setReasons([]);
            if (onReturnCreated) onReturnCreated();
        } catch (error) {
            toast.error(error.response?.data?.error || 'İade talebi oluşturulurken hata oluştu.');
        } finally {
            setSubmitting(false);
        }
    };

    const toggleItemSelection = (item) => {
        setSelectedItems(prev => {
            const exists = prev.find(i => i.product_id === item.product_id && i.variant_id === item.variant_id);
            if (exists) {
                return prev.filter(i => !(i.product_id === item.product_id && i.variant_id === item.variant_id));
            }
            return [...prev, { ...item, quantity: 1 }];
        });
    };

    const toggleReason = (reason) => {
        setReasons(prev => prev.includes(reason) ? prev.filter(r => r !== reason) : [...prev, reason]);
    };

    if (isCreateMode && orderToReturn) {
        return (
            <div className="bg-white rounded-3xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h2 className="text-2xl font-black text-gray-800">İade Talebi Oluştur</h2>
                        <p className="text-gray-500 mt-1">#{orderToReturn.order_number} numaralı siparişiniz için iade edilecek ürünleri seçin.</p>
                    </div>
                    <button 
                        onClick={onCancelReturnCreation}
                        className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
                    >
                        Geri Dön
                    </button>
                </div>

                <div className="space-y-6">
                    <div>
                        <h3 className="text-lg font-bold text-gray-800 mb-4">Ürünler</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {orderToReturn.order_items?.map((item, idx) => {
                                const isSelected = selectedItems.find(i => i.product_id === item.product_id && i.variant_id === item.variant_id);
                                return (
                                    <div 
                                        key={idx}
                                        onClick={() => toggleItemSelection(item)}
                                        className={`flex items-center p-4 rounded-2xl border-2 cursor-pointer transition-all duration-300 ${
                                            isSelected ? 'border-brand-green bg-brand-green/5' : 'border-gray-100 hover:border-gray-200'
                                        }`}
                                    >
                                        <div className="w-16 h-16 rounded-xl overflow-hidden bg-gray-50 flex-shrink-0 relative">
                                            {item.products?.main_image_url ? (
                                                <img src={item.products.main_image_url} alt={item.products?.name} className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-[10px] text-gray-400">Görsel Yok</div>
                                            )}
                                        </div>
                                        <div className="ml-4 flex-1">
                                            <h4 className="font-medium text-gray-800 line-clamp-2">{item.products?.name || 'İsimsiz Ürün'}</h4>
                                            {item.product_variants?.size && <p className="text-sm text-gray-500 mt-1">Beden: {item.product_variants.size}</p>}
                                        </div>
                                        <div className="ml-4">
                                            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${isSelected ? 'bg-brand-green text-white' : 'bg-gray-100 text-gray-300'}`}>
                                                {isSelected && <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div>
                        <h3 className="text-lg font-bold text-gray-800 mb-4">İade Nedeniniz</h3>
                        <div className="flex flex-wrap gap-2">
                            {RETURN_REASONS.map((reason, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => toggleReason(reason)}
                                    className={`px-4 py-2 rounded-xl text-sm font-medium border-2 transition-all duration-300 ${
                                        reasons.includes(reason) 
                                        ? 'border-brand-green bg-brand-green text-white' 
                                        : 'border-gray-100 bg-white text-gray-600 hover:border-gray-200'
                                    }`}
                                >
                                    {reason}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="pt-6 border-t border-gray-100">
                        <div className="bg-yellow-50 rounded-2xl p-4 mb-3 flex items-start">
                            <ExclamationTriangleIcon className="w-6 h-6 text-yellow-600 flex-shrink-0 mr-3" />
                            <p className="text-sm text-yellow-800">
                                İade talebiniz onaylandıktan sonra, ürünü size verilecek olan kargo referans numarası ile anlaşmalı kargo şubemize teslim edebilirsiniz. Ürünlerin etiketi koparılmamış, kullanılmamış ve yıkanmamış olmasına dikkat ediniz.
                            </p>
                        </div>
                        <div className="bg-blue-50 rounded-2xl p-4 mb-6 flex items-start">
                            <svg className="w-6 h-6 text-blue-600 flex-shrink-0 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            <p className="text-sm text-blue-800">
                                <strong>Önemli Bilgi:</strong> İade edilen ürünler tarafımıza ulaştıktan ve kontrol edildikten sonra, iade tutarınız <strong>4-5 iş günü içerisinde</strong> banka veya kredi kartınıza iade edilecektir.
                            </p>
                        </div>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={onCancelReturnCreation}
                                className="px-6 py-3 rounded-xl font-medium text-gray-600 hover:bg-gray-100 transition-colors"
                            >
                                İptal
                            </button>
                            <button
                                onClick={handleCreateReturn}
                                disabled={submitting || selectedItems.length === 0 || reasons.length === 0}
                                className="px-6 py-3 rounded-xl font-medium text-white bg-brand-green hover:bg-brand-green-light transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                            >
                                {submitting ? 'Oluşturuluyor...' : 'İade Talebi Oluştur'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h2 className="text-2xl font-black text-gray-800">İade Taleplerim</h2>
                    <p className="text-gray-500 mt-1">Geçmiş iade talepleriniz ve güncel durumları.</p>
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-green"></div>
                </div>
            ) : returns.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-3xl shadow-sm border border-gray-100">
                    <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                        <ArrowUturnLeftIcon className="w-10 h-10 text-gray-400" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">Henüz İade Talebiniz Yok</h3>
                    <p className="text-gray-500">Oluşturduğunuz iade talepleri burada listelenecektir.</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {returns.map((ret) => {
                        const statusInfo = getStatusInfo(ret.status);
                        const StatusIcon = statusInfo.icon;
                        const isExpanded = expandedReturnId === ret.id;

                        return (
                            <div key={ret.id} className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                                {/* Çekmece Başlığı (Tıklanabilir Alan) */}
                                <div 
                                    className="flex flex-col md:flex-row md:items-center justify-between gap-4 cursor-pointer"
                                    onClick={() => setExpandedReturnId(isExpanded ? null : ret.id)}
                                >
                                    <div>
                                        <div className="flex items-center gap-3 mb-2">
                                            <span className="text-sm font-bold text-gray-500">Talep No: #{ret.id}</span>
                                            <span className="text-sm text-gray-300">•</span>
                                            <span className="text-sm text-gray-500">{new Date(ret.created_at).toLocaleDateString('tr-TR')}</span>
                                        </div>
                                        <h3 className="text-lg font-bold text-gray-800">Sipariş: #{ret.orders?.order_number}</h3>
                                        
                                        {/* Ürün Görselleri (Çekmece Kapalıyken de Görünür) */}
                                        <div className="flex items-center gap-2 mt-3">
                                            {ret.return_items?.slice(0, 4).map((item, idx) => (
                                                <div key={idx} className="w-12 h-12 rounded-lg border border-gray-200 overflow-hidden shrink-0 bg-white">
                                                    {item.products?.main_image_url ? (
                                                        <img src={item.products.main_image_url} alt="ürün" className="w-full h-full object-cover" />
                                                    ) : (
                                                        <div className="w-full h-full bg-gray-50 flex items-center justify-center text-[10px] text-gray-400">Yok</div>
                                                    )}
                                                </div>
                                            ))}
                                            {ret.return_items?.length > 4 && (
                                                <div className="w-12 h-12 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center shrink-0">
                                                    <span className="text-xs font-bold text-gray-500">+{ret.return_items.length - 4}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <div className={`px-4 py-2 rounded-xl flex items-center gap-2 w-fit ${statusInfo.color}`}>
                                            <StatusIcon className="w-5 h-5" />
                                            <span className="font-bold text-sm">{statusInfo.label}</span>
                                        </div>
                                        <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 shrink-0">
                                            <svg className={`w-5 h-5 transform transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>

                                {/* Çekmece İçeriği */}
                                {isExpanded && (
                                    <div className="mt-6 pt-6 border-t border-gray-100">

                                {/* İade Tamamlandı Banner */}
                                {ret.status === 'completed' && (
                                    <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
                                        <div className="flex items-center">
                                            <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mr-4">
                                                <span className="text-white text-xl">🎉</span>
                                            </div>
                                            <div>
                                                <p className="font-bold text-green-800 text-lg">İade Tamamlandı!</p>
                                                <p className="text-sm text-green-600">İade işleminiz başarıyla tamamlanmıştır ve ücret iadeniz yapılmıştır.</p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* İade Nedeni ve İade Ödeme Bilgileri - Yan Yana */}
                                <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {ret.reason && (
                                        <div className="bg-orange-50/70 rounded-2xl p-5 border border-orange-100 flex flex-col justify-center min-w-0 overflow-hidden">
                                            <p className="text-xs font-bold text-orange-600 uppercase tracking-wider mb-2 flex items-center">
                                                <svg className="w-4 h-4 mr-1.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                İade Nedeni
                                            </p>
                                            <p className="text-orange-900 font-medium text-sm break-words leading-relaxed">{ret.reason}</p>
                                        </div>
                                    )}

                                    <div className="bg-green-50/70 rounded-2xl p-5 border border-green-100 flex flex-col justify-center min-w-0 overflow-hidden">
                                        <h4 className="text-xs font-bold text-green-700 uppercase tracking-wider mb-2 flex items-center">
                                            <svg className="w-4 h-4 mr-1.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            İade ve Geri Ödeme Bilgileri
                                        </h4>
                                        <div className="text-green-800 text-sm font-medium space-y-1.5">
                                            <p className="break-words leading-relaxed"><span className="opacity-80">Kargo:</span> Hepsijet (Kargonomi)</p>
                                            <p className="break-words leading-relaxed"><span className="opacity-80">Yöntem:</span> Orijinal ödeme yöntemi</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Yönetici Notu (Varsa alt satırda tam genişlik) */}
                                {ret.admin_notes && (
                                    <div className="mb-6 bg-blue-50/70 rounded-2xl p-5 border border-blue-100 min-w-0 overflow-hidden">
                                        <p className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-2 flex items-center">
                                            <svg className="w-4 h-4 mr-1.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            Yönetici Notu
                                        </p>
                                        <p className="text-blue-900 font-medium text-sm break-words leading-relaxed">{ret.admin_notes}</p>
                                    </div>
                                )}

                                {ret.customer_tracking_number && (
                                    <div className="bg-purple-50 rounded-2xl p-4 mb-6 border border-purple-100 flex items-center overflow-hidden">
                                        <div className="w-10 h-10 bg-purple-500 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                                            <span className="text-white text-lg">📦</span>
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-xs font-bold text-purple-600 uppercase tracking-wider mb-1">Kendi Gönderdiğiniz Kargo Takip No</p>
                                            <p className="text-purple-900 font-mono text-lg break-words">{ret.customer_tracking_number}</p>
                                        </div>
                                    </div>
                                )}

                                {(ret.status === 'approved' || ret.status === 'shipping') && ret.tracking_number && (
                                    <div className="mb-6 bg-blue-50 border border-blue-100 rounded-2xl p-5 overflow-hidden">
                                        <h4 className="text-sm font-bold text-blue-900 mb-2">Kargo Teslimat Bilgisi</h4>
                                        <p className="text-sm text-blue-800 mb-3 leading-relaxed">Lütfen aşağıdaki referans kodunu anlaşmalı kargo şubesine (Hepsijet/Kargonomi) vererek ürününüzü ücretsiz iade ediniz:</p>
                                        <div className="flex flex-wrap items-center gap-3 bg-white/60 p-3 rounded-xl border border-blue-200/60 w-fit max-w-full">
                                            <span className="text-xl font-black tracking-wider text-brand-green break-all">
                                                {ret.tracking_number.startsWith('RETURN-') ? (
                                                    <span className="text-base text-yellow-600 animate-pulse block truncate">Takip numarası alınıyor...</span>
                                                ) : ret.tracking_number}
                                            </span>
                                            {!ret.tracking_number.startsWith('RETURN-') && (
                                                <button 
                                                    onClick={() => {
                                                    navigator.clipboard.writeText(ret.tracking_number);
                                                    toast.success('Kod kopyalandı!');
                                                }}
                                                className="p-2 hover:bg-white rounded-lg transition-colors flex-shrink-0 border border-transparent hover:border-gray-200 shadow-sm"
                                                title="Kodu Kopyala"
                                            >
                                                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                                            </button>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div className="space-y-3 border-t border-gray-100 pt-4 mt-2">
                                    {ret.return_items?.map((item, idx) => (
                                        <div key={idx} className="flex flex-col sm:flex-row sm:items-center p-3 rounded-xl bg-gray-50 gap-3">
                                            <div className="flex items-center flex-1">
                                                <div className="w-12 h-12 rounded-lg overflow-hidden bg-white flex-shrink-0 border border-gray-200">
                                                    {item.products?.main_image_url ? (
                                                        <img src={item.products.main_image_url} alt={item.products.name} className="w-full h-full object-cover" />
                                                    ) : (
                                                        <div className="w-full h-full bg-gray-100 flex items-center justify-center text-xs text-gray-400">Görsel Yok</div>
                                                    )}
                                                </div>
                                                <div className="ml-4 flex-1">
                                                    <h4 className="text-sm font-bold text-gray-800 line-clamp-1">{item.products?.name}</h4>
                                                    <p className="text-xs text-gray-500 mt-0.5">Adet: {item.quantity}</p>
                                                </div>
                                            </div>
                                            {item.products && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        addToCart(item.products, item.variant_id || item.products.variants?.[0]?.id, 1);
                                                        toast.success('Ürün sepete eklendi!');
                                                    }}
                                                    className="w-full sm:w-auto px-4 py-2 bg-white border border-gray-200 text-brand-green font-semibold rounded-lg text-sm hover:bg-brand-green hover:text-white hover:border-brand-green transition-colors"
                                                >
                                                    Tekrar Satın Al
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ReturnsTab;
